import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useEmployeeActions, type PunchImportResult } from '../hooks/useBiometricAdmin';

/** Past punches from a spreadsheet exported from the machine (or its software). */
export function PunchImportModal({ onClose }: { onClose: () => void }) {
  const { importPunchesFile } = useEmployeeActions();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<PunchImportResult | null>(null);

  const submit = async () => {
    if (!file) return;
    try {
      const done = await importPunchesFile.mutateAsync(file);
      setResult(done);
      toast.success(`${done.accepted} punches imported.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Modal open onClose={onClose} title="Import past punches from Excel">
      <div className="space-y-4">
        <div className="space-y-1 text-sm text-on-surface-variant">
          <p>
            Use the punch export from the machine software (.xlsx, .xls or .csv). Each row needs the person&apos;s
            <strong> PIN / Enroll No</strong> and the <strong>date and time</strong> of the punch, in one column or two. A
            <strong> Status</strong> or <strong>VerifyMode</strong> column is used if present.
          </p>
          <p>
            Times are read as the time shown on the machine (India time). Days are written <em>day / month / year</em>. A
            punch already saved is skipped, so the same file can be imported twice safely. Attendance for the imported days is
            worked out straight away.
          </p>
        </div>

        <input
          ref={input}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setResult(null);
          }}
        />
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-outline-variant p-6 text-sm text-on-surface-variant hover:bg-surface-variant"
        >
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          {file ? file.name : 'Choose a spreadsheet'}
        </button>

        {result && (
          <div className="space-y-1 rounded-md border border-outline-variant bg-surface-variant/40 p-3 text-sm text-on-surface">
            <p>
              <strong>{result.accepted}</strong> new punches saved, <strong>{result.duplicates}</strong> already there,{' '}
              <strong>{result.rejected}</strong> rows could not be read.
            </p>
            {result.unmapped > 0 && (
              <p className="text-warning">
                {result.unmapped} punches belong to a PIN nobody has yet. Link them under Employees &gt; Unmapped PINs and their
                days will be built.
              </p>
            )}
            {result.rejected_samples.length > 0 && (
              <ul className="list-disc pl-5 text-xs text-on-surface-variant">
                {result.rejected_samples.map((r) => (
                  <li key={r.row}>
                    Row {r.row}: {r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          <Button onClick={submit} disabled={!file || importPunchesFile.isPending}>
            <Upload className="mr-1 h-4 w-4" />
            {importPunchesFile.isPending ? 'Importing…' : 'Import'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
